import React from 'react';
import {observable, action, computed, makeObservable, runInAction} from 'mobx';
import {observer} from 'mobx-react';
import store from '../../s';
import {checkout} from '../../../api';
import Message from '../msg';
import ModalLineCustom from '../modals/line-custom';
import ModalLineEP from '../modals/line-ep';
import ModalLineGradYear from '../modals/line-grad-year';
import Coord from './coord';
import TrustList from '../ab-step/ab-redesign-trust-list';
import {isCheckoutRedesign} from '../../ab-redesign';
import ModalSimple from '../../../global/modal-simple';
import AbTestStore, {ABTestKeys} from '../../../global/store/ab-test-store';
import ButtonMultilineShipments from './components/button-multiline-shipments/button-multiline-shipments';
import {currency, isNullOrWhiteSpace, Portal, ep_content_to_lines} from '../../../util';
import {ICartLineItem, ICheckoutMessageType, ICheckoutStep} from '../../i';
import {formatCartItemCount, getCartItemTotal} from './cart-quantity/cart-quantity';
import {showsFastShippingBadge} from './fast-shipping-badge';
import {CartModalHiResLoader} from './cart-modal-image/cart-modal-image-loader';
import {pushCartQtyChange, pushRemoveFromCart} from '../../ga4-cart';
import {sigEvent} from '../../../global/sig';
import {cartChangeQuantityLabel, cartDecreaseQuantityLabel, cartEditLabel, cartIncreaseQuantityLabel, cartQuantityGroupLabel, cartRemoveLabel} from './a11y/cart-line-label';

declare var modalOpen: any;

function isPreviewDetail(html: string, seen: {size: boolean; orient: boolean}): boolean {
    const text = html.replace(/<[^>]*>/g, '');
    if (!seen.size && /size:/i.test(text) && !/overall|printed|#2/i.test(text)) {
        seen.size = true;
        return true;
    }
    if (!seen.orient && /orientation:/i.test(text)) {
        seen.orient = true;
        return true;
    }
    return false;
}

@observer
class CartItemList extends React.Component {
    @observable lastEditedId: number = -1;
    @observable modalImageURL: string = '';
    @observable modalTitle: string = '';
    @observable modalImageLoading: boolean = false;
    @observable loading: boolean;
    @observable uniqueId: number;
    @observable showQtyInput: boolean = false;
    @observable expandedDetailIds: number[] = [];
    @observable debounceTimeout: ReturnType<typeof setTimeout> | null = null;
    cartModalImageLoader = new CartModalHiResLoader();
    /** Quantity last confirmed by the server per line id while a debounced edit is pending. */
    private syncedQty = new Map<number, number>();
    /** Set when skipStepsABTest moved a first-time visitor past the cart step: the cart step did not render, so no cart_step_view. */
    private skippedCartStep = false;

    constructor(props) {
        super(props);
        this.changeQty = this.changeQty.bind(this);
        this.removeProduct = this.removeProduct.bind(this);
        this.doModal = this.doModal.bind(this);
        this.loading = false;
        this.uniqueId = Math.floor(Math.random() * 100000001);
        this.getQtyInputVisibility();
        this.buyNowClicked();
        this.skipStepsABTest();
        makeObservable(this);
    }

    componentDidMount() {
        // The cart step really rendered (no Cart Skip): report it once per mount. The server keeps it to once per cart per session.
        if (!this.skippedCartStep && store.checkout.cstep === ICheckoutStep.cart && store.checkout.cart.items?.length) {
            sigEvent('cart_step_view', store.checkout.cart.pricing.total);
        }
    }

    componentWillUnmount() {
        this.cartModalImageLoader.invalidate();
    }

    @computed get cartItemTotal() {
        return getCartItemTotal(store.checkout.cart.items);
    }

    @action
    skipStepsABTest = async () => {
        if (store.checkoutABSkip && store.checkout.cart.items?.length && !store.performedSkip) {
            store.performedSkip = true;
            this.skippedCartStep = true;
            try {
                localStorage.setItem('performedSkip', 'true');
            } catch (_) {
                /* localStorage unavailable */
            }
            store.moveStep(ICheckoutStep.oci);

            store.uiCheckout.isLoadingEstimated = true;
            await checkout('get-est');

            runInAction(() => {
                // approved ria
                store.uiCheckout.isLoadingEstimated = false;
            });
        }
    };

    @action
    getQtyInputVisibility = () => {
        this.showQtyInput = store.checkout.cart.items.some(i => i.qty > 10);
    };

    @action
    changeQty = async (e: React.ChangeEvent<HTMLSelectElement> | React.ChangeEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>, id: number, forceUpdate: boolean = false) => {
        const target = e.target as HTMLInputElement;
        let newQty = parseInt(target.value, 10);
        const item = store.checkout.cart.items.filter(o => o.id === id)[0];

        this.lastEditedId = id;
        const oldQty = item.qty;

        if (newQty === -1) {
            this.showQtyInput = true;
            newQty = 10;
        } else if (newQty === 0 || isNaN(newQty)) {
            return;
        }

        // Debounced edits replace each other, so the GA4 delta (and a failure rollback) must be
        // measured from the quantity the server last confirmed, not from the previous keystroke.
        if (!this.syncedQty.has(id)) {
            this.syncedQty.set(id, oldQty);
        }
        item.qty = newQty;

        const change = async () => {
            this.loading = true;
            const previousQty = this.syncedQty.get(id) ?? oldQty;
            this.syncedQty.delete(id);
            const success = await checkout('update-qty');

            if (!success) {
                item.qty = previousQty;
            } else {
                pushCartQtyChange(item, previousQty, newQty);
            }

            this.cartStatusChanged(item.qty);
            this.loading = false;
        };

        const setDebounceTime = (this.showQtyInput || item.qty > 10) && +target.value !== -1;

        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
        }

        if (forceUpdate || !setDebounceTime) {
            await change();
        } else {
            this.debounceTimeout = setTimeout(async () => {
                await change();
            }, 2000);
        }
    };

    @action
    removeProduct = async (id: number) => {
        this.loading = true;
        const item = store.checkout.cart.items.filter(o => o.id === id)[0];
        this.lastEditedId = id;
        const oldQty = item.qty;
        item.qty = 0;
        const success = await checkout('update-qty');
        if (!success) {
            item.qty = oldQty;
        } else {
            pushRemoveFromCart(item, oldQty);
        }

        this.cartStatusChanged();
        this.loading = false;
    };

    cartStatusChanged(quantity: number = 0) {
        const element = document.getElementById('cart-status-changed');

        if (!element) {
            return;
        }

        if (quantity) {
            element.textContent = `Product quantity has been changed to ${quantity}`;
        } else {
            element.textContent = 'Product has been removed from the cart';
        }
    }

    @action
    doModal(o: ICartLineItem) {
        this.modalTitle = `${o.title1} ${o.title2}`;
        this.modalImageURL = o.imgFull;
        this.modalImageLoading = true;
        modalOpen('modal-cart-image-main');
        this.cartModalImageLoader.load(o.imgFull, {
            onSuccess: action((hiResUrl: string) => {
                this.modalImageURL = hiResUrl;
                this.modalImageLoading = false;
            }),
            onError: action(() => {
                this.modalImageLoading = false;
            }),
        });
    }

    @action
    toggleDetails = (id: number) => {
        if (this.expandedDetailIds.includes(id)) {
            this.expandedDetailIds = this.expandedDetailIds.filter(x => x !== id);
        } else {
            this.expandedDetailIds.push(id);
        }
    };

    @action
    stepQty = (id: number, next: number) => {
        if (next < 1) {
            return;
        }
        this.changeQty({target: {value: String(next)}} as React.ChangeEvent<HTMLInputElement>, id, true);
    };

    @action
    buyNowClicked() {
        const param = new URLSearchParams(window.location.search).get('buyNow');

        if (param && +param === 1) {
            store.moveStep(ICheckoutStep.oci);
        }
    }

    render() {
        // console.log(toJS(store.checkout.cart.items));
        const checkoutRedesign = isCheckoutRedesign(store.checkout.isMyFrames);

        return (
            <div>
                <div
                    className="checkout-header"
                    style={{color: Portal?.PortalID === 3 ? '#33393a' : '', backgroundColor: Portal?.PortalID === 3 ? '#edece9' : ''}}
                >
                    {checkoutRedesign ? (
                        <>
                            <span className="ab-cart-title">Cart</span>
                            <span className="ab-cart-count">{formatCartItemCount(this.cartItemTotal)}</span>
                        </>
                    ) : (
                        <span className="d-none d-md-block">Your Cart: {formatCartItemCount(this.cartItemTotal)}</span>
                    )}
                </div>
                <div className={store.checkout.cstep <= ICheckoutStep.checkout && !checkoutRedesign ? 'd-none d-md-block cart-desktop' : 'cart-desktop'}>
                    {store.checkout.cart.items.map((o, _p) => {
                        // Redesign keeps the actions on screen while the cart updates: every row is inert and faded, with a spinner centered on it.
                        const rowLoading = checkoutRedesign && this.loading;
                        const specSeen = {size: false, orient: false};
                        const detailIsPreview = o.details.map(d => isPreviewDetail(d, specSeen));
                        const detailsOpen = this.expandedDetailIds.includes(o.id);
                        const hasCollapsedDetails = detailIsPreview.some(preview => !preview) || o.hasEp || o.hasCustom || (!o.IsDYF && o.gradYear > 0) || o.discountHighlight !== '' || !isNullOrWhiteSpace(o.special_msg) || o.messages.some(y => y.msgType === ICheckoutMessageType.backorder);
                        return (
                            <div
                                className={`row item-detail-row${detailsOpen ? ' is-expanded' : ''}${rowLoading ? ' is-loading' : ''}`}
                                key={_p}
                                ref={el => {
                                    if (el) {
                                        (el as HTMLElement & {inert: boolean}).inert = rowLoading;
                                    }
                                }}
                            >
                                <div className="col-9">
                                    <div className="cd-item">
                                        <div className="img-space">
                                            <img
                                                height={400}
                                                width={400}
                                                tabIndex={0}
                                                src={o.imgThumb.replace('_200_200.png', '_400_400.png').replace('images.d', 'images2.d').replace('com//', 'com/')}
                                                alt={`${o.title1} ${o.title2}`}
                                                className={`resp float-left p-overlay-master${!store.checkout.isMyFrames && o.is_fast_shipping ? ' border-fast-shipping' : ''}`}
                                                loading="lazy"
                                                onClick={() => this.doModal(o)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        this.doModal(o);
                                                    }
                                                }}
                                            />
                                            {showsFastShippingBadge(o, store.checkout.isMyFrames) &&
                                                (checkoutRedesign ? (
                                                    <div className="fast-ship">
                                                        <span className="ship-text">Fast shipping</span>
                                                    </div>
                                                ) : (
                                                    <div className={`fast-shipping fast-shipping-${Portal?.PortalID}`}>
                                                        FAST SHIPPING <i className={Portal?.PortalID !== 3 ? `fas fa-shipping-fast` : `fas fa-cart-shopping-fast`}></i>
                                                    </div>
                                                ))}
                                        </div>
                                        <div className="non-image-space">
                                            <div className="title1 b">
                                                <a
                                                    href={o.URL}
                                                    aria-label={`View Cart Product ${o.title1}`}
                                                >
                                                    {o.title1}
                                                </a>
                                            </div>
                                            <div className="title2">
                                                <a
                                                    href={o.URL}
                                                    aria-label={`View Cart Product ${o.title2}`}
                                                >
                                                    {o.title2}
                                                </a>
                                            </div>
                                            <div className="priceDetail">
                                                {o.qty > 1 ? `Qty: ${o.qty} @` : ''} {currency(o.price)}
                                            </div>
                                            {o.qty > 1 && <div className="price">{currency(o.price * o.qty)}</div>}
                                            {/* <div className='more' data-toggle='collapse' data-target={`#details-cd-${o.id.toString()}`}>details</div> */}
                                            <div
                                                id={`details-cd-${o.id.toString()}`}
                                                className="pad-top"
                                                style={{paddingLeft: 6}}
                                            >
                                                <div className="details">
                                                    {o.details.map((d, i) => (
                                                        <div
                                                            key={i}
                                                            className={detailIsPreview[i] ? 'ab-spec' : 'ab-more'}
                                                            dangerouslySetInnerHTML={{__html: d}}
                                                        />
                                                    ))}
                                                    {o.hasEp && (
                                                        <div className="ab-more">
                                                            <div>
                                                                <ModalLineEP
                                                                    id={o.id}
                                                                    modalId={`modal-line-ep-${o.id}`}
                                                                />
                                                                <span className="details-title">{o.epTitle}</span>
                                                                {o.epMethodId < 2 && (
                                                                    <button
                                                                        type="button"
                                                                        className="link text-btn"
                                                                        style={{
                                                                            paddingLeft: 4,
                                                                            fontSize: 12,
                                                                        }}
                                                                        aria-label={cartEditLabel(o.epTitle, o.title1, o.title2, 'engraved plate')}
                                                                        onClick={() => modalOpen(`modal-line-ep-${o.id}`)}
                                                                    >
                                                                        <i
                                                                            className="fas fa-edit link pointer"
                                                                            aria-hidden={true}
                                                                        ></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <div style={{paddingLeft: 6}}>
                                                                {o.ep1 && <div>{o.ep1}</div>}
                                                                {o.ep2 && <div>{o.ep2}</div>}
                                                                {o.ep3 && <div>{o.ep3}</div>}
                                                                {o.ep4 && <div>{o.ep4}</div>}
                                                                {o.ep5 && <div>{o.ep5}</div>}
                                                                {o.ep6 && <div>{o.ep6}</div>}
                                                                {o.ep7 && <div>{o.ep7}</div>}
                                                                {o.ep8 && <div>{o.ep8}</div>}
                                                                {o.ep9 && <div>{o.ep9}</div>}
                                                                {o.ep10 && <div>{o.ep10}</div>}
                                                            </div>
                                                            {o.epMethodId == 3 && (
                                                                <ul style={{marginBottom: 0}}>
                                                                    {ep_content_to_lines(o.epContent).map((eee, i) => (
                                                                        <li>
                                                                            Line {i + 1}: {eee}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            )}
                                                            {o.epWidth && o.epHeight && (
                                                                <div>
                                                                    <span className="details-title">Plate Size:</span> {o.epWidth}"w x {o.epHeight}"h
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {o.hasCustom && (
                                                        <div className="ab-more">
                                                            <div>
                                                                <ModalLineCustom
                                                                    id={o.id}
                                                                    modalId={`modal-line-custom-${o.id}`}
                                                                />
                                                                {((o.CustomField1_Title.length > 0 && o.CustomField1_Title.trim().length > 0 && o.CustomField1.length > 0) || o.productId === 88937) && (
                                                                    <div>
                                                                        {o.productId !== 88937 && (
                                                                            <span
                                                                                className="details-title"
                                                                                style={{
                                                                                    color: '#616161',
                                                                                }}
                                                                            >
                                                                                {o.CustomField1_Title}:
                                                                            </span>
                                                                        )}{' '}
                                                                        {o.CustomField1}
                                                                        <button
                                                                            style={{
                                                                                paddingLeft: 4,
                                                                                fontSize: 12,
                                                                            }}
                                                                            className="link text-btn"
                                                                            type="button"
                                                                            aria-label={`Edit ${o.CustomField1_Title}`}
                                                                            onClick={() => modalOpen(`modal-line-custom-${o.id}`)}
                                                                        >
                                                                            <i
                                                                                className="fas fa-edit link pointer"
                                                                                aria-hidden={true}
                                                                            ></i>
                                                                        </button>
                                                                    </div>
                                                                )}
                                                                {o.CustomField2_Title.length > 0 && o.CustomField2_Title.trim().length > 0 && o.CustomField2.length > 0 && (
                                                                    <div>
                                                                        <span
                                                                            className="details-title"
                                                                            style={{
                                                                                color: '#616161',
                                                                            }}
                                                                        >
                                                                            {o.CustomField2_Title}:
                                                                        </span>{' '}
                                                                        {o.CustomField2}
                                                                        <button
                                                                            style={{
                                                                                paddingLeft: 4,
                                                                                fontSize: 12,
                                                                            }}
                                                                            className="link text-btn"
                                                                            type="button"
                                                                            aria-label={`Edit ${o.CustomField2_Title}`}
                                                                            onClick={() => modalOpen(`modal-line-custom-${o.id}`)}
                                                                        >
                                                                            <i
                                                                                className="fas fa-edit link pointer"
                                                                                aria-hidden={true}
                                                                            ></i>
                                                                        </button>
                                                                    </div>
                                                                )}
                                                                {o.CustomField3_Title.length > 0 && o.CustomField3_Title.trim().length > 1 && o.CustomField3.length > 0 && (
                                                                    <div>
                                                                        <span
                                                                            className="details-title"
                                                                            style={{
                                                                                color: '#616161',
                                                                            }}
                                                                        >
                                                                            {o.CustomField3_Title}:
                                                                        </span>{' '}
                                                                        {o.CustomField3}
                                                                        <button
                                                                            style={{
                                                                                paddingLeft: 4,
                                                                                fontSize: 12,
                                                                            }}
                                                                            className="link text-btn"
                                                                            type="button"
                                                                            aria-label={`Edit ${o.CustomField3_Title}`}
                                                                            onClick={() => modalOpen(`modal-line-custom-${o.id}`)}
                                                                        >
                                                                            <i
                                                                                className="fas fa-edit link pointer"
                                                                                aria-hidden={true}
                                                                            ></i>
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {!o.IsDYF && o.gradYear > 0 && (
                                                        <div className="ab-more">
                                                            <div>
                                                                <ModalLineGradYear
                                                                    id={o.id}
                                                                    modalId={`modal-line-grad-year-${o.id}-${this.uniqueId}`}
                                                                />
                                                                <span
                                                                    className="details-title"
                                                                    style={{color: '#616161'}}
                                                                >
                                                                    Grad Year:
                                                                </span>{' '}
                                                                {o.gradYear.toString()}
                                                                <button
                                                                    style={{
                                                                        paddingLeft: 4,
                                                                        fontSize: 12,
                                                                    }}
                                                                    type="button"
                                                                    aria-label="Edit Year of Graduation"
                                                                    className="link text-btn"
                                                                    onClick={() => modalOpen(`modal-line-grad-year-${o.id}-${this.uniqueId}`)}
                                                                >
                                                                    <i
                                                                        className="fas fa-edit link pointer"
                                                                        aria-hidden={true}
                                                                    ></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {o.discountHighlight !== '' && (
                                                        <div
                                                            className="discount-highlight ab-more"
                                                            dangerouslySetInnerHTML={{
                                                                __html: o.discountHighlight,
                                                            }}
                                                        />
                                                    )}
                                                    {!isNullOrWhiteSpace(o.special_msg) && (
                                                        <div
                                                            className="discount-highlight ab-more"
                                                            style={{textAlign: 'left'}}
                                                        >
                                                            {o.special_msg}
                                                        </div>
                                                    )}

                                                    {o.messages
                                                        .filter(y => y.msgType === ICheckoutMessageType.backorder)
                                                        .map(x => (
                                                            <div className="alert alert-info ab-more">{x.msg}</div>
                                                        ))}
                                                </div>
                                                {hasCollapsedDetails && (
                                                    <button
                                                        type="button"
                                                        className="ab-expand text-btn"
                                                        aria-expanded={detailsOpen}
                                                        onClick={() => this.toggleDetails(o.id)}
                                                    >
                                                        {detailsOpen ? 'Collapse details' : 'Expand details'} {detailsOpen ? '−' : '+'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {(!this.loading || checkoutRedesign) && (
                                    <div className="col-3 text-right">
                                        <div className="cart-desktop-price">
                                            {/* Redesign shows the line total; the control keeps the unit price (its hidden Qty/@ lines carry the rest). */}
                                            {checkoutRedesign ? currency(o.price * o.qty) : currency(o.price)}
                                            {checkoutRedesign && o.qty > 1 && (
                                                <span className="ab-unit-price">
                                                    {o.qty} × {currency(o.price)}
                                                </span>
                                            )}
                                        </div>
                                        {o.VolumeDiscountAmt > 0 && (
                                            <div className="volume">
                                                Saving <b>{currency(o.VolumeDiscountAmt)}</b> with {o.VolumeDiscountPercent}% volume discount
                                            </div>
                                        )}
                                        {o.productId !== 24791 && (
                                            <div
                                                className="form-inline"
                                                style={{justifyContent: 'flex-end'}}
                                            >
                                                {!this.showQtyInput && o.qty <= 10 && (
                                                    <select
                                                        className="form-select change-qty"
                                                        aria-label={cartChangeQuantityLabel(o.title1, o.title2, 'Change Quantity')}
                                                        value={o.qty}
                                                        name={`qty-${_p.toString()}`}
                                                        onChange={e => this.changeQty(e, o.id)}
                                                    >
                                                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, -1].map((q, _i) => (
                                                            <option
                                                                key={_i}
                                                                value={q}
                                                            >
                                                                {q === -1 ? 'custom' : q.toString()}
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                                {(this.showQtyInput || o.qty > 10) && (
                                                    <input
                                                        className="form-control change-qty"
                                                        aria-label={cartChangeQuantityLabel(o.title1, o.title2, 'Change Quantity')}
                                                        value={o.qty}
                                                        name={`qty-tb-${_p.toString()}`}
                                                        onChange={e => this.changeQty(e, o.id, false)}
                                                        onBlur={e => {
                                                            if (+e.target.value !== o.qty) {
                                                                this.changeQty(e, o.id, true);
                                                            }
                                                        }}
                                                        onKeyDown={e => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                this.changeQty(e, o.id, true);
                                                            }
                                                        }}
                                                    />
                                                )}
                                                {this.lastEditedId === o.id && (
                                                    <div className="ab-qty-msg">
                                                        <Message msgType={ICheckoutMessageType.cart_qty} />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <button
                                            className="remove text-btn"
                                            type="button"
                                            onClick={() => this.removeProduct(o.id)}
                                            aria-label={cartRemoveLabel(o.title1, o.title2, 'Remove product from cart')}
                                        >
                                            <i
                                                className="fas fa-times ab-remove-icon"
                                                aria-hidden={true}
                                            />
                                            <span className="ab-remove-label">Remove</span>
                                        </button>

                                        {o.storeId === 205164 && (
                                            <div className="ab-extra">
                                                <ButtonMultilineShipments cartItem={o} />
                                            </div>
                                        )}

                                        <div className="ab-actions">
                                            {o.productId !== 24791 && (
                                                <div
                                                    className="ab-qty"
                                                    role="group"
                                                    aria-label={cartQuantityGroupLabel(o.title1, o.title2)}
                                                >
                                                    <button
                                                        type="button"
                                                        aria-label={cartDecreaseQuantityLabel(o.title1, o.title2)}
                                                        disabled={o.qty <= 1}
                                                        onClick={() => this.stepQty(o.id, o.qty - 1)}
                                                    >
                                                        <i className="far fa-minus"></i>
                                                    </button>
                                                    <span>{o.qty}</span>
                                                    <button
                                                        type="button"
                                                        aria-label={cartIncreaseQuantityLabel(o.title1, o.title2)}
                                                        onClick={() => this.stepQty(o.id, o.qty + 1)}
                                                    >
                                                        <i className="far fa-plus"></i>
                                                    </button>
                                                </div>
                                            )}
                                            {Portal?.PortalID !== 3 && (
                                                <div
                                                    className="remove"
                                                    style={{paddingTop: '24px'}}
                                                >
                                                    <a
                                                        href={o.URL}
                                                        className="btn-checkout btn-checkout-white"
                                                        aria-label="Edit Product"
                                                        style={{
                                                            fontSize: '15px',
                                                            fontWeight: 400,
                                                            color: '#171717',
                                                        }}
                                                    >
                                                        <i
                                                            className="far fa-pen ab-edit-icon"
                                                            aria-hidden={true}
                                                        />
                                                        <span className="ab-edit-label">Edit</span>
                                                        <span className="ab-edit-legacy">{o.MouldingID > 0 ? 'Edit Frame' : 'Edit'}</span>
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {rowLoading && (
                                    <div
                                        className="ab-loading"
                                        role="status"
                                        aria-label="Updating cart"
                                    >
                                        <span
                                            className="ab-spinner"
                                            aria-hidden={true}
                                        />
                                    </div>
                                )}
                                {this.loading && !checkoutRedesign && (
                                    <div className="col-3 text-right">
                                        <div className="cart-desktop-price">{currency(o.price)}</div>
                                        <div
                                            className="form-inline"
                                            style={{paddingRight: '12%', paddingTop: '4%'}}
                                        >
                                            <i
                                                className="fas fa-circle-notch fa-spin fa-2x"
                                                aria-hidden={true}
                                            />
                                        </div>
                                    </div>
                                )}
                                {store.checkout.cart.items.length > 1 && (
                                    <div className="col-12">
                                        <hr />
                                    </div>
                                )}
                                <div
                                    id="cart-status-changed"
                                    className="sr-only"
                                    aria-live="polite"
                                ></div>
                            </div>
                        );
                    })}

                    {!checkoutRedesign && store.checkout.cart.items && store.checkout.cart.items.length > 0 && !AbTestStore.ABIsVariant(ABTestKeys.ab_checkout_1, 1) && (
                        <div
                            style={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                fontSize: 14,
                                marginTop: 16,
                            }}
                        >
                            <div>
                                <picture>
                                    <source
                                        type="image/webp"
                                        srcSet={`https://${Portal?.CDNDomain}/images/cached/general/ppa25.webp`}
                                    />
                                    <img
                                        width="94"
                                        height="26"
                                        loading="lazy"
                                        src={`https://${Portal?.CDNDomain}/images/cached/general/ppa25.jpg`}
                                        alt="PayPal Pay Later"
                                        style={{height: 16, width: 'auto'}}
                                    />
                                </picture>
                            </div>

                            <div style={{fontSize: 14, paddingLeft: 6, marginTop: 2}}>
                                Pay in 4 payments of {currency(store.checkout.cart.pricing.total / 4)}.{' '}
                                <a
                                    href="https://www.paypal.com/us/digital-wallet/ways-to-pay/buy-now-pay-later"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="link"
                                    style={{fontSize: 14, textDecoration: 'underline'}}
                                >
                                    Learn More.
                                </a>
                            </div>
                        </div>
                    )}

                    {!checkoutRedesign && (!store.checkout.cart.items || store.checkout.cart.items.length < 2) && <div className="hr"></div>}

                    {!checkoutRedesign && store.checkout.cart.items && store.checkout.cart.items.length > 0 && store.checkout.cstep === ICheckoutStep.cart && (
                        <div className="continue-shopping">
                            <a
                                href={Portal && Portal.PortalID < 3 ? store.getContinueShoppingLink() : '/'}
                                className="underline"
                                aria-label="Continue Shopping"
                            >
                                Continue Shopping {store.checkout.cart.items.length > 0 && store.checkout.cart.items[0].title1}
                            </a>
                        </div>
                    )}

                    {(!store.checkout.cart.items || store.checkout.cart.items.length < 1) && checkoutRedesign && (
                        <div className="ab-empty-cart">
                            <div className="ab-empty-hero">
                                <span
                                    className="ab-empty-icon"
                                    aria-hidden={true}
                                >
                                    <i className="far fa-cart-shopping" />
                                </span>
                                <div className="ab-empty-title">Your cart is empty</div>
                                <div className="ab-empty-text">{Portal?.PortalID === 0 ? 'Find your school or organization, browse our best sellers, or design a frame of your own to get started.' : 'Browse our frames to find the one for you.'}</div>
                                <a
                                    href={Portal?.PortalID === 0 ? '/shop-frames-gifts' : '/'}
                                    className="ab-empty-cta"
                                >
                                    Continue shopping
                                </a>
                            </div>
                            {Portal?.PortalID === 0 && (
                                <>
                                    <div className="ab-empty-tiles-title">Popular ways to shop</div>
                                    <nav
                                        className="ab-empty-tiles"
                                        aria-label="Popular ways to shop"
                                    >
                                        <a
                                            href="/best-sellers"
                                            className="ab-empty-tile"
                                        >
                                            <i
                                                className="far fa-star"
                                                aria-hidden={true}
                                            />
                                            <span>Best sellers</span>
                                        </a>
                                        <a
                                            href="/shop-frames-gifts/college-university-diploma-frames.aspx"
                                            className="ab-empty-tile"
                                        >
                                            <i
                                                className="far fa-graduation-cap"
                                                aria-hidden={true}
                                            />
                                            <span>College &amp; graduation</span>
                                        </a>
                                        <a
                                            href="/professional-certificate-license-frames"
                                            className="ab-empty-tile"
                                        >
                                            <i
                                                className="far fa-briefcase"
                                                aria-hidden={true}
                                            />
                                            <span>Professional &amp; license frames</span>
                                        </a>
                                        <a
                                            href="/frame-designer"
                                            className="ab-empty-tile"
                                        >
                                            <i
                                                className="far fa-pen-ruler"
                                                aria-hidden={true}
                                            />
                                            <span>Create your own custom frame</span>
                                        </a>
                                    </nav>
                                    <div className="ab-empty-links">
                                        <a
                                            href="/about-our-frames/frames-custom-made-for-you.aspx"
                                            title="About Our Frames"
                                        >
                                            Learn more about our frames
                                        </a>
                                    </div>
                                    <TrustList className="ab-empty-trust" />
                                </>
                            )}
                        </div>
                    )}

                    {(!store.checkout.cart.items || store.checkout.cart.items.length < 1) && !checkoutRedesign && (
                        <div className="text-center">
                            <div
                                className="form-inline-label"
                                style={{fontSize: 28}}
                            >
                                Your cart is empty.
                            </div>
                            {Portal?.PortalID === 3 && (
                                <div
                                    className="link"
                                    style={{marginTop: 36}}
                                >
                                    <a
                                        href="/"
                                        title="Diploma Frames"
                                    >
                                        Visit our Framerly Shop
                                    </a>
                                </div>
                            )}
                            {Portal?.PortalID === 0 && (
                                <div
                                    className="link"
                                    style={{marginTop: 36}}
                                >
                                    <a
                                        href="/shop-frames-gifts.aspx"
                                        title="Diploma Frames"
                                    >
                                        Visit our Shop Frames and Gifts page
                                    </a>
                                </div>
                            )}
                            {Portal?.PortalID === 0 && (
                                <div
                                    className="link"
                                    style={{marginTop: 36}}
                                >
                                    <a
                                        href="/frame-designer"
                                        title="Custom Frame"
                                    >
                                        Create your own Custom Frame
                                    </a>
                                </div>
                            )}
                            {Portal?.PortalID === 0 && (
                                <div
                                    className="link"
                                    style={{marginTop: 36}}
                                >
                                    <a
                                        href="/about-our-frames/frames-custom-made-for-you.aspx"
                                        title="About Our Frames"
                                    >
                                        Learn more about our frames
                                    </a>
                                </div>
                            )}
                        </div>
                    )}

                    {Portal && Portal.PortalID < 1 && !checkoutRedesign && <Coord />}
                </div>

                <ModalSimple
                    id="modal-cart-image-main"
                    title={`${this.modalTitle}`}
                >
                    <div style={{position: 'relative'}}>
                        <img
                            src={this.modalImageURL}
                            alt={`${this.modalTitle}`}
                            className="resp"
                        />
                        {this.modalImageLoading && (
                            <div style={{position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.5)'}}>
                                <i
                                    className="fas fa-circle-notch fa-spin fa-2x"
                                    aria-hidden={true}
                                />
                            </div>
                        )}
                    </div>
                </ModalSimple>
            </div>
        );
    }
}

export default CartItemList;
